import React, { useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { PreorderResult, TrackProductInput } from '../types';
import { TrackDialog } from './TrackDialog';
import { addTrackedProduct } from '../utils/api';
import { useTrackedStores } from '../hooks/useTrackedStores';

interface Props {
  results: PreorderResult[];
  message?: string;
  loading: boolean;
  onProductTracked: () => void;
}

const STATUS_COLORS: Record<string, string> = {
  Available: '#22c55e',
  'Coming Soon': '#eab308',
  'Not Yet Listed': '#8a93b0',
  'Out of Stock': '#ef4444',
};

export function ResultsPanel({ results, message, loading, onProductTracked }: Props) {
  const [trackTarget, setTrackTarget] = useState<PreorderResult | null>(null);
  const [trackMsg, setTrackMsg] = useState('');
  const { trackStoreByName, untrackStore, isTrackedByName, stores } = useTrackedStores();
  const isMobile = useIsMobile();

  async function handleTrack(input: TrackProductInput) {
    try {
      await addTrackedProduct(input);
      setTrackTarget(null);
      setTrackMsg(`"${input.productTitle}" added to tracked products.`);
      onProductTracked();
      setTimeout(() => setTrackMsg(''), 4000);
    } catch (err) {
      setTrackMsg(err instanceof Error ? err.message : 'Failed to track product.');
    }
  }

  function handleSaveStore(r: PreorderResult) {
    if (isTrackedByName(r.storeName)) {
      const existing = stores.find((s) => s.name.toLowerCase() === r.storeName.toLowerCase());
      if (existing) untrackStore(existing.id);
    } else {
      trackStoreByName(r.storeName, r.region, r.storeUrl);
    }
  }

  if (loading) return <div style={styles.empty}>Searching preorder listings...</div>;

  if (results.length === 0) {
    return (
      <div style={styles.empty}>
        {message ?? 'Run a search to see preorder listings.'}
      </div>
    );
  }

  const trackedCount = results.filter((r) => isTrackedByName(r.storeName)).length;

  return (
    <div>
      {trackMsg && <div style={styles.toast}>{trackMsg}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <p style={styles.count}>
          {results.length} listing{results.length !== 1 ? 's' : ''}
          {trackedCount > 0 && (
            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#eab308' }}>
              ★ {trackedCount} from saved stores
            </span>
          )}
        </p>
      </div>

      <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {results
          .slice()
          .sort((a, b) => {
            // 1. Saved stores first (best-match anchor)
            const aT = isTrackedByName(a.storeName) ? 0 : 1;
            const bT = isTrackedByName(b.storeName) ? 0 : 1;
            if (aT !== bT) return aT - bT;
            // 2. In-stock before coming-soon before unlisted
            const statusOrder: Record<string, number> = { Available: 0, 'Coming Soon': 1, 'Not Yet Listed': 2, 'Out of Stock': 3 };
            const sDiff = (statusOrder[a.stockStatus] ?? 9) - (statusOrder[b.stockStatus] ?? 9);
            if (sDiff !== 0) return sDiff;
            // 3. Cheapest first (null prices last)
            const pa = a.price ?? Infinity;
            const pb = b.price ?? Infinity;
            return pa - pb;
          })
          .map((r) => {
            const saved = isTrackedByName(r.storeName);
            return (
              <div
                key={`${r.productId}-${r.storeName}`}
                style={{ ...styles.card, ...(saved ? styles.cardTracked : {}) }}
              >
                <div style={styles.cardHeader}>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={styles.badge}>{r.region}</span>
                    {saved && (
                      <span style={styles.savedBadge}>★ Saved store</span>
                    )}
                  </div>
                  <span style={{ ...styles.status, color: STATUS_COLORS[r.stockStatus] ?? '#8a93b0' }}>
                    {r.stockStatus}
                  </span>
                </div>

                <div style={styles.title}>{r.productTitle}</div>
                <div style={styles.sub}>{r.productType}</div>

                <div style={styles.row}>
                  <span style={styles.muted}>Store</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <a href={r.storeUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                      {r.storeName}
                    </a>
                    <button
                      title={saved ? 'Remove from saved stores' : 'Save this store'}
                      style={{ ...styles.starBtn, ...(saved ? styles.starBtnActive : {}) }}
                      onClick={() => handleSaveStore(r)}
                    >
                      {saved ? '★' : '☆'}
                    </button>
                  </div>
                </div>

                <div style={styles.row}>
                  <span style={styles.muted}>Price</span>
                  <span style={styles.price}>
                    {r.price !== null ? `${r.currency} ${r.price.toFixed(2)}` : 'TBD'}
                  </span>
                </div>

                <div style={styles.row}>
                  <span style={styles.muted}>Release</span>
                  <span>
                    {r.releaseDate ?? 'TBA'}
                    {r.releaseDateStatus === 'Unconfirmed' && (
                      <span style={{ color: '#eab308', marginLeft: '4px', fontSize: '11px' }}>unconfirmed</span>
                    )}
                  </span>
                </div>

                <div style={styles.row}>
                  <span style={styles.muted}>Updated</span>
                  <span style={{ fontSize: '12px', color: '#8a93b0' }}>
                    {new Date(r.lastCheckedAt).toLocaleString()}
                  </span>
                </div>

                <button style={styles.trackBtn} onClick={() => setTrackTarget(r)}>
                  + Track this product
                </button>
              </div>
            );
          })}
      </div>

      {trackTarget && (
        <TrackDialog product={trackTarget} onConfirm={handleTrack} onClose={() => setTrackTarget(null)} />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  empty: { padding: '40px', textAlign: 'center', color: '#8a93b0', fontSize: '15px' },
  count: { color: '#8a93b0', fontSize: '13px', margin: 0 },
  toast: {
    padding: '12px 16px', background: '#22c55e20', border: '1px solid #22c55e',
    borderRadius: '8px', color: '#22c55e', marginBottom: '16px', fontSize: '14px',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card: {
    background: '#1a1d27', borderRadius: '10px', padding: '18px',
    border: '1.5px solid #2e3347', display: 'flex', flexDirection: 'column', gap: '10px',
    transition: 'border-color 0.15s',
  },
  cardTracked: {
    borderColor: '#eab308',
  },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge: {
    background: '#f9731620', color: '#f97316', fontSize: '11px',
    fontWeight: 700, padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.05em',
  },
  savedBadge: {
    background: '#eab30820', color: '#eab308', fontSize: '11px',
    fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
  },
  status: { fontSize: '12px', fontWeight: 600 },
  title: { fontSize: '14px', fontWeight: 700, lineHeight: 1.4 },
  sub: { fontSize: '12px', color: '#8a93b0' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' },
  muted: { color: '#8a93b0' },
  price: { fontWeight: 700, color: '#f97316', fontSize: '15px' },
  link: { color: '#3b82f6', textDecoration: 'none', fontSize: '13px' },
  starBtn: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: '16px', color: '#8a93b0', padding: '0 2px', lineHeight: 1,
  },
  starBtnActive: { color: '#eab308' },
  trackBtn: {
    marginTop: 'auto', padding: '12px', background: '#f9731615',
    border: '1.5px solid #f97316', borderRadius: '7px', color: '#f97316',
    fontWeight: 600, cursor: 'pointer', fontSize: '14px', minHeight: '44px',
  },
};
